import { useCallback, useEffect, useState } from "react";
import { Send, CheckCircle2, XCircle, Trash2, Pencil, Loader2, FileText, ShieldCheck, Save } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import api from "../../lib/api";

const fmt = (iso) => { try { return new Date(iso).toLocaleString("de-DE"); } catch { return iso || "—"; } };

const TEXT_FIELDS = [
    { key: "invite_subject", label: "Einladung – Betreff", area: false },
    { key: "invite_body", label: "Einladung – Text ({vehicle} = Fahrzeug)", area: true },
    { key: "reminder_subject", label: "Erinnerung – Betreff", area: false },
    { key: "reminder_body", label: "Erinnerung – Text ({vehicle})", area: true },
    { key: "due_today_subject", label: "Endet heute – Betreff", area: false },
    { key: "due_today_body", label: "Endet heute – Text ({vehicle}, {end_date})", area: true },
    { key: "overdue1_subject", label: "1–2 Wochen überfällig – Betreff", area: false },
    { key: "overdue1_body", label: "1–2 Wochen überfällig – Text ({vehicle}, {end_date})", area: true },
    { key: "overdue2_subject", label: "3–4 Wochen überfällig – Betreff", area: false },
    { key: "overdue2_body", label: "3–4 Wochen überfällig – Text ({vehicle}, {end_date})", area: true },
    { key: "securing_subject", label: "Sicherstellung – Betreff", area: false },
    { key: "securing_body", label: "Sicherstellung – Text ({vehicle}, {end_date})", area: true },
    { key: "secure_company_subject", label: "Sicherstellung an Leasinggeber – Betreff ({vehicle}, {contract})", area: false },
    { key: "secure_company_body", label: "Sicherstellung an Leasinggeber – Text ({vehicle}, {vin}, {contract}, {end_date}, {customer})", area: true },
    { key: "landing_intro", label: "Landingpage – Einleitung", area: true },
    { key: "landing_takeover_text", label: "Landingpage – Text Übernahme", area: true },
    { key: "landing_return_text", label: "Landingpage – Text Rückgabe", area: true },
    { key: "leasing_contact_phone", label: "Leasing-Kontakt – Telefon (Kopf-/Fußzeile Landingpage)", area: false },
    { key: "leasing_contact_email", label: "Leasing-Kontakt – E-Mail (Kopf-/Fußzeile Landingpage)", area: false },
];

function TextsConfig({ focusField }) {
    const [texts, setTexts] = useState(null);
    const [busy, setBusy] = useState(false);
    const load = useCallback(() => api.get("/leasing-returns/texts").then((r) => setTexts(r.data.texts)).catch(() => {}), []);
    useEffect(() => { load(); }, [load]);
    // Sprung von „Vorlage bearbeiten" (Kontaktstrecke) → passendes Feld hervorheben.
    useEffect(() => {
        if (!focusField || !texts) return;
        const el = document.querySelector(`[data-testid="leasing-text-${focusField}_subject"]`);
        if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
            el.classList.add("ring-2", "ring-[#FFCC00]");
            setTimeout(() => el.classList.remove("ring-2", "ring-[#FFCC00]"), 2500);
        }
    }, [focusField, texts]);
    const set = (k, v) => setTexts((p) => ({ ...p, [k]: v }));
    const save = async () => {
        setBusy(true);
        try { await api.put("/leasing-returns/texts", texts); toast.success("Texte gespeichert."); }
        catch { toast.error("Speichern fehlgeschlagen."); }
        finally { setBusy(false); }
    };
    if (!texts) return <Loader2 className="w-4 h-4 animate-spin text-gray-400" />;
    return (
        <div className="space-y-3" data-testid="leasing-texts-config">
            {TEXT_FIELDS.map((f) => (
                <div key={f.key}>
                    <label className="text-[10px] uppercase font-bold text-gray-500">{f.label}</label>
                    {f.area
                        ? <Textarea value={texts[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} rows={2} className="rounded-sm text-sm" data-testid={`leasing-text-${f.key}`} />
                        : <Input value={texts[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} className="rounded-sm text-sm" data-testid={`leasing-text-${f.key}`} />}
                </div>
            ))}
            <div className="border rounded-sm p-3 bg-emerald-50/40 space-y-2" data-testid="leasing-financing-config">
                <label className="flex items-center gap-2 text-sm font-semibold">
                    <input type="checkbox" checked={!!texts.financing_enabled} onChange={(e) => set("financing_enabled", e.target.checked)} data-testid="leasing-financing-enabled" />
                    Online-Finanzierung (Ratenrechner) für die Übernahme aktivieren
                </label>
                {texts.financing_enabled && (
                    <div className="grid grid-cols-3 gap-2 pt-1">
                        <div>
                            <label className="text-[10px] uppercase font-bold text-gray-500">Effektivzins % p.a.</label>
                            <Input type="number" step="0.01" value={texts.financing_annual_rate ?? ""} onChange={(e) => set("financing_annual_rate", e.target.value)} className="rounded-sm text-sm" data-testid="leasing-financing-rate" />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase font-bold text-gray-500">Laufzeit min. (Mon.)</label>
                            <Input type="number" value={texts.financing_term_min ?? ""} onChange={(e) => set("financing_term_min", e.target.value)} className="rounded-sm text-sm" data-testid="leasing-financing-term-min" />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase font-bold text-gray-500">Laufzeit max. (Mon.)</label>
                            <Input type="number" value={texts.financing_term_max ?? ""} onChange={(e) => set("financing_term_max", e.target.value)} className="rounded-sm text-sm" data-testid="leasing-financing-term-max" />
                        </div>
                        <p className="col-span-3 text-[11px] text-gray-500">Der Kunde wählt im Ratenrechner Anzahlung, Laufzeit (12–96) und optionale Schlussrate (Ballon). Zusätzlich füllt er die digitale Selbstauskunft aus.</p>
                    </div>
                )}
            </div>
            <div className="border rounded-sm p-3 bg-blue-50/50 space-y-2" data-testid="leasing-autosend-config">
                <label className="flex items-center gap-2 text-sm font-semibold">
                    <input type="checkbox" checked={!!texts.email_autosend} onChange={(e) => set("email_autosend", e.target.checked)} data-testid="leasing-autosend-enabled" />
                    <Send className="w-4 h-4 text-blue-600" />
                    E-Mail-Auto-Versand: alle Leasing-Mails ohne weitere Freigabe senden
                </label>
                <p className="text-[11px] text-gray-500">Solange aktiv, werden neue Einladungen & Erinnerungen automatisch versendet (ohne Freigabe). Bestehende Entwürfe sendest du gesammelt über „Alle Entwürfe senden" in der E-Mail-Freigabe oben. ⚠️ Versand aktuell gemockt (kein Resend-Key) – wird protokolliert.</p>
            </div>
            <label className="flex items-center gap-2 text-sm pt-1" data-testid="leasing-text-require-verification">
                <input type="checkbox" checked={!!texts.require_verification} onChange={(e) => set("require_verification", e.target.checked)} />
                <ShieldCheck className="w-4 h-4 text-gray-500" />
                Landingpage durch <b>Nachname + Geburtsdatum</b> schützen (für Go-Live; aktuell aus → Zugang nur per Link)
            </label>
            <div className="flex justify-end">
                <Button onClick={save} disabled={busy} className="rounded-sm bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="leasing-texts-save">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Texte speichern
                </Button>
            </div>
        </div>
    );
}

function DraftRow({ d, onChanged }) {
    const [busy, setBusy] = useState(false);
    const [edit, setEdit] = useState(false);
    const [f, setF] = useState({ to_email: d.to_email, subject: d.subject, body: d.body });
    const run = async (fn) => { setBusy(true); try { await fn(); onChanged(); } catch (e) { toast.error(e?.response?.data?.detail || "Aktion fehlgeschlagen."); } finally { setBusy(false); } };
    const approve = () => run(async () => { const { data } = await api.post(`/leasing-returns/outbox/${d.id}/approve`); toast.success(data.mocked ? "Freigegeben & versendet (GEMOCKT – kein Resend-Key)." : "Freigegeben & versendet."); });
    const reject = () => run(async () => { await api.post(`/leasing-returns/outbox/${d.id}/reject`); toast.success("Entwurf verworfen."); });
    const del = () => run(async () => { await api.delete(`/leasing-returns/outbox/${d.id}`); toast.success("Gelöscht."); });
    const saveEdit = () => run(async () => { await api.patch(`/leasing-returns/outbox/${d.id}`, f); toast.success("Entwurf aktualisiert."); setEdit(false); });
    const isDraft = d.status === "draft";
    const badge = { draft: ["bg-amber-100 text-amber-800", "Entwurf"], sent: ["bg-emerald-100 text-emerald-800", "Versendet"], rejected: ["bg-gray-100 text-gray-500", "Verworfen"] }[d.status] || ["bg-gray-100", d.status];
    return (
        <div className="border rounded-sm p-3 bg-white" data-testid={`leasing-outbox-row-${d.id}`}>
            <div className="flex items-center gap-2 text-sm flex-wrap">
                <Send className="w-3.5 h-3.5 text-indigo-600" />
                <span className="font-bold truncate">{d.subject}</span>
                <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded-sm ${badge[0]}`}>{badge[1]}</span>
                <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded-sm bg-indigo-50 text-indigo-700">{d.type === "reminder" ? "Erinnerung" : "Einladung"}</span>
                <span className="ml-auto text-[11px] text-gray-400">{d.sent_at ? `versendet ${fmt(d.sent_at)}` : fmt(d.created_at)}</span>
            </div>
            <div className="text-[11px] text-gray-500 mt-0.5">An {d.to_email || <span className="text-red-500">keine E-Mail</span>} · {d.to_name} · {d.vehicle_label}</div>
            {edit ? (
                <div className="mt-2 space-y-2 border-t pt-2">
                    <Input value={f.to_email} onChange={(e) => setF((p) => ({ ...p, to_email: e.target.value }))} placeholder="E-Mail" className="rounded-sm text-sm" data-testid={`leasing-outbox-edit-email-${d.id}`} />
                    <Input value={f.subject} onChange={(e) => setF((p) => ({ ...p, subject: e.target.value }))} placeholder="Betreff" className="rounded-sm text-sm" data-testid={`leasing-outbox-edit-subject-${d.id}`} />
                    <Textarea value={f.body} onChange={(e) => setF((p) => ({ ...p, body: e.target.value }))} rows={3} className="rounded-sm text-sm" data-testid={`leasing-outbox-edit-body-${d.id}`} />
                    <div className="flex justify-end gap-2">
                        <Button onClick={() => setEdit(false)} variant="ghost" className="rounded-sm h-8 text-xs text-gray-500">Abbrechen</Button>
                        <Button onClick={saveEdit} disabled={busy} className="rounded-sm h-8 text-xs bg-indigo-600 text-white" data-testid={`leasing-outbox-edit-save-${d.id}`}>Speichern</Button>
                    </div>
                </div>
            ) : (
                <div className="text-xs text-gray-600 mt-1 line-clamp-2 whitespace-pre-line">{d.body}</div>
            )}
            {isDraft && !edit && (
                <div className="flex flex-wrap gap-2 mt-2 pt-2 border-t">
                    <Button onClick={approve} disabled={busy} className="rounded-sm h-8 text-xs bg-green-700 hover:bg-green-800 text-white font-bold" data-testid={`leasing-outbox-approve-${d.id}`}>
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />} Freigeben & senden
                    </Button>
                    <Button onClick={() => setEdit(true)} variant="outline" className="rounded-sm h-8 text-xs" data-testid={`leasing-outbox-editbtn-${d.id}`}><Pencil className="w-3.5 h-3.5 mr-1" /> Bearbeiten</Button>
                    <Button onClick={reject} variant="ghost" className="rounded-sm h-8 text-xs text-amber-700" data-testid={`leasing-outbox-reject-${d.id}`}><XCircle className="w-3.5 h-3.5 mr-1" /> Verwerfen</Button>
                    <Button onClick={del} variant="ghost" className="rounded-sm h-8 text-xs text-red-500 ml-auto" data-testid={`leasing-outbox-del-${d.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
            )}
        </div>
    );
}

export default function LeasingFreigabe({ focusField }) {
    return (
        <div className="space-y-6" data-testid="leasing-freigabe-view">
            <div className="text-[11px] text-gray-600 bg-blue-50 border border-blue-200 rounded-sm px-3 py-2 flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-blue-600 shrink-0" />
                <span>Die <b>Freigabe & Automatik</b> der Leasing-Mails läuft jetzt zentral über die
                    <a href="/dealer/email-freigabe?source=leasing" className="font-bold underline decoration-[#FFCC00] decoration-2 ml-1" data-testid="leasing-central-freigabe-link">E-Mail-Freigabe</a>
                    (Bereich „Leasing"). Hier bearbeitest du nur noch die <b>Texte</b>.</span>
            </div>
            <section className="border rounded-sm bg-white">
                <div className="px-4 py-3 border-b flex items-center gap-2">
                    <FileText className="w-4 h-4 text-indigo-600" /><h3 className="font-black text-sm">Texte (E-Mails & Landingpage)</h3>
                </div>
                <div className="p-4"><TextsConfig focusField={focusField} /></div>
            </section>
        </div>
    );
}
